import { Grid } from 'src/design/atoms/Grid';
import { Group } from 'src/design/atoms/Group';
import { Paper } from 'src/design/atoms/Paper';
import { SimpleGrid } from 'src/design/atoms/SimpleGrid';
import { Stack } from 'src/design/atoms/Stack';
import { Text } from 'src/design/atoms/Text';
import { Title } from 'src/design/atoms/Title';
import { Radio } from 'src/design/atoms/Radio';
import { Textarea } from 'src/design/atoms/Textarea';
import { TextInput } from 'src/design/atoms/TextInput';
import { nanoid } from 'nanoid';
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useJobs } from 'src/hooks/useJobs';
import { EditButton } from 'src/design/atoms/EditButton';
import { LinkButton } from 'src/design/atoms/LinkButton';
import { Currency, formatCurrency } from 'src/design/atoms/Currency';
import { Header } from 'src/design/organisms/Header';
import { PlaceSelector } from 'src/app/selectors/PlaceSelector';
import { getErrorMessage, logger } from 'src/utils';
import { useAuth } from 'src/hooks/useAuth';
import { WeatherCard } from 'src/routes/Jobs/Weather/WeatherCard';
import { ActivityFeedCard } from './ActivityFeedCard';
import { ChangesSummary } from './ChangesSummary';
import { ContactsCard } from './ContactsCard';
import { DateSummaryCard } from './DateSummaryCard';
import { EditDetails } from './EditDetails';
import { Installations } from './Installations';
import { MilestoneCard } from './MilestoneCard';
import { PhysicalProgressInput } from './Progress';
import { RFISummary } from './RFISummary';
import { TeamCard } from './TeamCard';
import { ViewCTCJobButton } from './components/ViewCTCJobButton';

export const Summary: React.FC = () => {
  const { selectedJob, rehydrateJob, jobSummary, loadSummary } = useJobs();
  const [editing, setEditing] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [progressLastUpdated, setProgressLastUpdated] = React.useState(
    new Date(),
  );
  const navigate = useNavigate();
  const { checkFeatureAllowed } = useAuth();
  const hasCTCAccess = checkFeatureAllowed(['modules_all', 'ctc']);
  const hasPhysicalProgress = checkFeatureAllowed([
    'job_summary_all',
    'job_summary_view_progress',
  ]);
  const hasPhysicalProgressUpdate = checkFeatureAllowed([
    'job_summary_all',
    'job_summary_update_progress',
  ]);
  const hasEstimatedVsActual = checkFeatureAllowed([
    'job_summary_all',
    'job_summary_estimated_vs_actual',
  ]);
  const hasManhours = checkFeatureAllowed([
    'job_summary_all',
    'job_summary_view_manhours',
  ]);
  const hasEditDetails = checkFeatureAllowed([
    'job_summary_all',
    'job_summary_update_details',
  ]);
  const hasActivity = checkFeatureAllowed(['jobs_all', 'jobs_activity']);
  const hasMilestones = checkFeatureAllowed(['jobs_all', 'jobs_milestones']);
  const hasRFIs = checkFeatureAllowed(['jobs_all', 'jobs_milestones']);
  const hasContacts = checkFeatureAllowed(['jobs_all', 'jobs_contacts']);
  const hasChanges = checkFeatureAllowed(['jobs_all', 'jobs_changes']);

  React.useEffect(() => {
    try {
      loadSummary();
    } catch (err) {
      logger.error('Failed to load summary', { err });
      setError(getErrorMessage(err));
    }
  }, []);

  const getType = (v: string) => {
    const items = v.split('-');
    return items[1] ? items[1] : 'N/A';
  };

  if (!selectedJob) return null;

  const isServiceJob = selectedJob.jobType === 'service';

  const renderHyperlinks = () => {
    if (!selectedJob.hyperlinks || selectedJob.hyperlinks.length === 0)
      return <Text>No Hyperlinks Assigned</Text>;
    return selectedJob.hyperlinks.map((h) => {
      return (
        <LinkButton key={h.id || nanoid()} to={h.url} target="_blank">
          {h.title ?? ''}
        </LinkButton>
      );
    });
  };

  return (
    <Stack gap="md">
      <Header
        title={`${selectedJob?.name} - ${selectedJob?.jobNumber}`}
        breadcrumbProps={{
          items: [
            { text: 'Jobs', href: '/jobs' },
            {
              text: `${selectedJob?.name} - ${selectedJob?.jobNumber}`,
              href: `/jobs/${selectedJob?.id}/summary`,
            },
            { text: 'Summary' },
          ],
        }}
        helpText="View and manage job details, progress, and related information"
      />
      {error && <Text c="red">{error}</Text>}
      {hasCTCAccess && !isServiceJob && (
        <Group justify="flex-end">
          <ViewCTCJobButton jobNumber={selectedJob?.jobNumber} />
        </Group>
      )}
      <Grid>
        <Grid.Col span={{ base: 12, md: 5 }}>
          <Stack gap="md">
            <Paper p="md">
              <Group justify="space-between" mb="md">
                <Title order={5}>Job Details</Title>
                {!editing && hasEditDetails && (
                  <EditButton size="xs" onClick={() => setEditing(true)} />
                )}
              </Group>

              {editing ? (
                <EditDetails
                  job={selectedJob}
                  onCancel={() => setEditing(false)}
                  onSave={rehydrateJob}
                />
              ) : (
                <Stack gap="md">
                  <TextInput
                    label="Job Number - Job Name"
                    value={`${selectedJob?.jobNumber} - ${selectedJob?.name}`}
                    disabled
                  />
                  <TextInput
                    label="Customer"
                    value={selectedJob?.customerName}
                    disabled
                  />
                  <TextInput
                    label="Division"
                    value={
                      selectedJob?.divisionEntity?.name || selectedJob?.division
                    }
                    disabled
                  />
                  <TextInput
                    label="Job Type"
                    value={
                      isServiceJob
                        ? 'Service'
                        : getType(
                            selectedJob?.divisionEntity?.name ||
                              selectedJob?.division,
                          )
                    }
                    disabled
                  />
                  <Textarea
                    label="Scope of Work"
                    value={selectedJob?.description}
                    disabled
                    autosize
                    minRows={2}
                  />

                  <SimpleGrid cols={2}>
                    <Radio.Group
                      label="Tax Exempt"
                      value={selectedJob?.taxExempt ? 'true' : 'false'}
                    >
                      <Group mt="xs">
                        <Radio value="true" label="Yes" disabled />
                        <Radio value="false" label="No" disabled />
                      </Group>
                    </Radio.Group>

                    <Radio.Group
                      label="Vendor Discounts"
                      value={selectedJob?.vendorDiscounts ? 'true' : 'false'}
                    >
                      <Group mt="xs">
                        <Radio value="true" label="Yes" disabled />
                        <Radio value="false" label="No" disabled />
                      </Group>
                    </Radio.Group>
                  </SimpleGrid>

                  <SimpleGrid cols={2}>
                    <Radio.Group
                      label="Scale Pay"
                      value={selectedJob?.scalePayEnabled ? 'true' : 'false'}
                    >
                      <Group mt="xs">
                        <Radio value="true" label="Yes" disabled />
                        <Radio value="false" label="No" disabled />
                      </Group>
                    </Radio.Group>

                    {!isServiceJob && (
                      <Radio.Group
                        label="Bonded"
                        value={
                          jobSummary.ctcJob?.bonded === 'yes' ? 'true' : 'false'
                        }
                      >
                        <Group mt="xs">
                          <Radio value="true" label="Yes" disabled />
                          <Radio value="false" label="No" disabled />
                        </Group>
                      </Radio.Group>
                    )}
                  </SimpleGrid>

                  <TextInput
                    label="Per Diem"
                    value={
                      selectedJob?.perDiemEnabled
                        ? formatCurrency(selectedJob?.perDiemAmount)
                        : 'N/A'
                    }
                    disabled
                  />

                  <PlaceSelector
                    label="Jobsite Location"
                    initialValue={selectedJob?.location}
                    disabled
                  />

                  <Title order={6}>Project Hyperlinks</Title>
                  {renderHyperlinks()}
                </Stack>
              )}
            </Paper>

            {!isServiceJob && jobSummary.ctcJob && (
              <Paper p="md">
                <Group justify="space-between" mb="md">
                  <Title order={5}>CTC Job Details</Title>
                  <ViewCTCJobButton
                    jobNumber={selectedJob?.jobNumber}
                    size="xs"
                  />
                </Group>
                <Stack gap="xs">
                  <Text>
                    <b>Contract Amount:</b>{' '}
                    <Currency value={jobSummary.ctcJob.contractAmount} />
                  </Text>
                  <Text>
                    <b>Customer:</b> {jobSummary.ctcJob.customer}
                  </Text>
                  <Text>
                    <b>Division:</b> {jobSummary.ctcJob.division}
                  </Text>
                  <Text>
                    <b>Salesperson:</b> {jobSummary.ctcJob.salesperson}
                  </Text>
                  <Text>
                    <b>Certified Payroll:</b>{' '}
                    {jobSummary.ctcJob.certifiedPayroll}
                  </Text>
                  <Text>
                    <b>OCIP:</b> {jobSummary.ctcJob.ocip}
                  </Text>
                  <Text>
                    <b>Liquidated Damages:</b> {jobSummary.ctcJob.lds}
                  </Text>
                </Stack>
              </Paper>
            )}

            {hasMilestones && (
              <MilestoneCard
                title="Milestones"
                items={jobSummary.milestones}
                jobId={selectedJob.id}
              />
            )}

            {hasRFIs && <RFISummary jobId={selectedJob.id} />}
            {hasChanges && <ChangesSummary jobId={selectedJob.id} />}
            {hasContacts && (
              <ContactsCard
                jobId={selectedJob.id}
                customerNumber={selectedJob.customerNumber}
                customerName={selectedJob.customerName}
              />
            )}
            <TeamCard jobId={selectedJob.id} />
          </Stack>
        </Grid.Col>

        <Grid.Col span={{ base: 12, md: 7 }}>
          {selectedJob && (
            <Stack gap="md">
              {hasActivity && (
                <SimpleGrid cols={2}>
                  <ActivityFeedCard
                    title="Constraints"
                    items={jobSummary.constraints}
                  />
                  <ActivityFeedCard
                    title="Activity By Due Date"
                    items={jobSummary.due}
                  />
                </SimpleGrid>
              )}

              {hasPhysicalProgress && (
                <PhysicalProgressInput
                  canEdit={hasPhysicalProgressUpdate}
                  jobId={selectedJob.id}
                  jobNumber={selectedJob.jobNumber}
                  initialProgress={[]}
                  onProgressSaved={() => {
                    setProgressLastUpdated(new Date());
                  }}
                />
              )}

              {hasEstimatedVsActual && (
                <Installations
                  jobId={selectedJob.id}
                  jobNumber={selectedJob.jobNumber}
                  lastUpdated={progressLastUpdated}
                  isServiceJob={isServiceJob}
                />
              )}

              {hasManhours && (
                <DateSummaryCard
                  jobId={selectedJob.id}
                  jobNumber={selectedJob.jobNumber}
                  isServiceJob={isServiceJob}
                />
              )}

              <WeatherCard job={selectedJob} />
            </Stack>
          )}
        </Grid.Col>
      </Grid>
    </Stack>
  );
};
